import Eyebrow from '@Guidelines/Component/Eyebrow';
import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import FieldRowActions from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Sheet/FieldRowActions';
import describeField from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Sheet/Helpers/describeField';
import type SubjectFieldDraftView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectFieldDraftView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';

const rowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.875rem;
	flex-wrap: wrap;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.colors.surfaceMuted};
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.3125rem;
	flex: 1;
	min-width: 13.75rem;
`;

const labelStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.90625rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.35;
	color: ${theme.colors.ink};
`;

type Props = {
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	readonly index: number;
	readonly field: SubjectFieldDraftView;
	readonly canMoveUp: boolean;
	readonly canMoveDown: boolean;
	readonly isReadOnly: boolean;
	readonly serverMessage: string | null;
};

/** One active field: its label, its nature and states in capitals, its actions, and the server error that targets it. */
const FieldRow: React.FC<Props> = ({
	onChange,
	index,
	field,
	canMoveUp,
	canMoveDown,
	isReadOnly,
	serverMessage,
}) => {
	const natures = useSelector((state) => state.subjectTypeDefinitionsStore.fieldNatures);
	const natureLabel = natures?.find((nature) => nature.value === field.nature)?.label ?? field.natureLabel;
	const summary = React.useMemo(() => describeField(field, natureLabel), [field, natureLabel]);

	return (
		<div>
			<div css={rowStyle}>
				<div css={textStyle}>
					<span css={labelStyle}>{field.label}</span>
					<Eyebrow>{summary}</Eyebrow>
				</div>
				<FieldRowActions
					onChange={onChange}
					index={index}
					label={field.label}
					isNew={field.key === null}
					canMoveUp={canMoveUp}
					canMoveDown={canMoveDown}
					isReadOnly={isReadOnly}
				/>
			</div>
			<ConditionalLoader data={serverMessage}>
				{(message) => <small className="ui-kit-field-error">{message}</small>}
			</ConditionalLoader>
		</div>
	);
};

export default FieldRow;
